import { useState, useCallback } from 'react';
import { 
  Payment, 
  PaymentSearchParams, 
  PaymentListResponse,
  PaymentStats
} from '../models/PaymentModels';
import { PaymentService } from '../services/PaymentService';

// 결제 관리 ViewModel 상태 인터페이스
interface PaymentViewModelState {
  // 결제 목록 관련
  payments: Payment[];
  loading: boolean;
  error: string | null;
  
  // 페이지네이션 관련
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  
  // 검색 및 필터 관련
  searchQuery: string;
  yearMonth: string;
  sortBy: string;
  order: 'asc' | 'desc';
  
  // 통계 관련
  stats: PaymentStats | null;
  statsLoading: boolean;
  statsError: string | null;
}

// 결제 관리 ViewModel 훅
export const usePaymentViewModel = () => {
  // 상태 관리
  const [state, setState] = useState<PaymentViewModelState>({
    payments: [],
    loading: false,
    error: null,
    currentPage: 1,
    totalPages: 0,
    total: 0,
    limit: 15,
    searchQuery: '',
    yearMonth: new Date().toISOString().slice(0, 7), // 현재 연월 (YYYY-MM)
    sortBy: 'create_datetime',
    order: 'desc',
    stats: null,
    statsLoading: false,
    statsError: null
  });

  // 결제 목록과 통계를 함께 조회 (한 번의 API 호출로 중복 방지)
  const fetchPaymentsAndStats = useCallback(async (params?: Partial<PaymentSearchParams>) => {
    try {
      // API 호출 시작 시 로딩 상태 설정. 에러 메시지는 초기화.
      setState(prev => ({ 
        ...prev, 
        loading: true, 
        statsLoading: true, 
        error: null, 
        statsError: null 
      }));

      // searchParams 구성: 전달된 params를 최우선으로 사용하고, 없으면 현재 상태(state) 사용
      // 이렇게 함으로써 함수 호출 시점의 최신 필터값을 명시적으로 사용할 수 있음.
      const searchParams: PaymentSearchParams = {
        year_month: params?.year_month !== undefined ? params.year_month : state.yearMonth,
        q: params?.q !== undefined ? params.q : state.searchQuery,
        sort_by: params?.sort_by || state.sortBy,
        order: params?.order || state.order,
        page: params?.page || state.currentPage,
        limit: params?.limit || state.limit
      };

      console.log('🔄 ViewModel: fetchPaymentsAndStats 호출됨. 결정된 searchParams:', searchParams);

      let result: { stats: PaymentStats; listResponse: PaymentListResponse };
      try {
        result = await PaymentService.getPaymentStatsAndList(searchParams);
        console.log('✅ ViewModel: PaymentService.getPaymentStatsAndList 성공:', result);
      } catch (apiError) {
        console.warn('⚠️ ViewModel: PaymentService.getPaymentStatsAndList 실패. 목업 데이터 사용 시도:', apiError);
        const mockPayments = PaymentService.getMockPayments(searchParams);
        const mockStats = PaymentService.getMockPaymentStats(searchParams.year_month !== undefined ? searchParams.year_month : state.yearMonth);
        result = {
          stats: mockStats,
          listResponse: mockPayments
        };
        setState(prev => ({ 
          ...prev, 
          error: '결제 목록을 불러오는 중 오류가 발생했습니다. 목업 데이터를 표시합니다.',
          statsError: '결제 통계를 불러오는 중 오류가 발생했습니다. 목업 데이터를 표시합니다.'
        }));
      }

      // API 호출 완료 후 상태 업데이트
      setState(prev => ({
        ...prev,
        payments: result.listResponse.payments,
        total: result.listResponse.total,
        totalPages: result.listResponse.totalPages,
        currentPage: result.listResponse.page, // API 응답의 page 값으로 현재 페이지 동기화
        limit: result.listResponse.limit,     // API 응답의 limit 값으로 동기화 (필요시)
        loading: false,
        stats: result.stats,
        statsLoading: false,
        // API 호출에 사용된 searchParams 값으로 ViewModel 상태를 최종 동기화
        // undefined인 경우 이전 상태(prev) 값을 유지하여 타입 오류 방지
        yearMonth: searchParams.year_month !== undefined ? searchParams.year_month : prev.yearMonth,
        searchQuery: searchParams.q !== undefined ? searchParams.q : prev.searchQuery,
        sortBy: searchParams.sort_by || prev.sortBy, // params에 없으면 이전 값 유지
        order: searchParams.order || prev.order,     // params에 없으면 이전 값 유지
      }));

      console.log('✅ ViewModel: 상태 업데이트 완료. 동기화된 현재 상태:', {
        paymentsCount: result.listResponse.payments.length,
        stats: result.stats,
        currentPage: result.listResponse.page,
        yearMonth: searchParams.year_month, 
        searchQuery: searchParams.q
      });

    } catch (error: any) {
      console.error('❌ ViewModel: fetchPaymentsAndStats 처리 중 전체 에러:', error);
      setState(prev => ({
        ...prev,
        payments: [], 
        total: 0,
        totalPages: 0,
        loading: false,
        stats: null,
        statsLoading: false,
        error: '결제 데이터를 불러오는데 실패했습니다.',
        statsError: '결제 통계를 불러오는데 실패했습니다.'
      }));
    }
  }, [state.yearMonth, state.searchQuery, state.sortBy, state.order, state.currentPage, state.limit]);

  // 검색어 변경
  const setSearchQuery = useCallback((query: string) => {
    console.log('📝 ViewModel: setSearchQuery 호출됨. query:', query);
    setState(prev => ({ ...prev, searchQuery: query, currentPage: 1 }));
  }, [setState]);

  // 연월 필터 변경
  const setYearMonth = useCallback((yearMonth: string) => {
    console.log('📅 ViewModel: setYearMonth 호출됨. yearMonth:', yearMonth);
    setState(prev => ({ ...prev, yearMonth, currentPage: 1 }));
  }, [setState]);

  // 정렬 변경
  const setSorting = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    setState(prev => ({ ...prev, sortBy, order, currentPage: 1 }));
  }, [setState]);

  // 페이지 변경
  const setCurrentPage = useCallback((page: number) => {
    setState(prev => ({ ...prev, currentPage: page }));
  }, [setState]);

  // 페이지 크기 변경
  const setLimit = useCallback((limit: number) => {
    setState(prev => ({ ...prev, limit, currentPage: 1 }));
  }, [setState]);

  // 검색 실행
  const handleSearch = useCallback(() => {
    console.log('🔍 ViewModel: handleSearch 호출됨 (주의: 실제 API 호출은 PaymentsPage에서 fetchPaymentsAndStats를 통해 이루어져야 함)', 
                { currentSearchQuery: state.searchQuery, currentYearMonth: state.yearMonth });
  }, [state.searchQuery, state.yearMonth]);

  // 필터 초기화
  const resetFilters = useCallback(() => {
    const defaultYearMonth = new Date().toISOString().slice(0, 7);
    console.log('🔄 ViewModel: resetFilters 호출됨. 기본값으로 설정.');
    setState(prev => ({
      ...prev,
      searchQuery: '',
      yearMonth: defaultYearMonth,
      sortBy: 'create_datetime',
      order: 'desc',
      currentPage: 1
    }));
    // 실제 데이터 리로드는 PaymentsPage에서 이 함수 호출 후 fetchPaymentsAndStats를 호출하여 수행
  }, [setState]);

  // 에러 메시지 제거
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, [setState]);

  // 통계 에러 메시지 제거
  const clearStatsError = useCallback(() => {
    setState(prev => ({ ...prev, statsError: null }));
  }, [setState]);

  return {
    // 상태
    ...state,
    
    // 액션
    fetchPaymentsAndStats,
    setSearchQuery,
    setYearMonth,
    setSorting,
    setCurrentPage,
    setLimit,
    handleSearch,
    resetFilters,
    clearError,
    clearStatsError
  };
}; 